import type {MouseEventHandler, ReactElement} from 'react';
import {useState} from 'react';
import styles from './ai-chat-sidebar-nav-item.module.scss';
import type {AiChatSidebarNavItemProps} from './ai-chat-sidebar-nav-item.types';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';

export function AiChatSidebarNavItem({icon, label, onClick, hasCircledIcon = false, href}: AiChatSidebarNavItemProps): ReactElement {
  const [isHovered, setIsHovered] = useState(false);

  const hoverHandlers = {
    onMouseEnter: (): void => {
      setIsHovered(true);
    },
    onMouseLeave: (): void => {
      setIsHovered(false);
    },
    onFocus: (): void => {
      setIsHovered(true);
    },
    onBlur: (): void => {
      setIsHovered(false);
    },
  };

  const handleClick: MouseEventHandler<HTMLElement> = (e): void => {
    const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey;
    if (href !== undefined && isModifiedClick) {
      return;
    }

    onClick(e);
  };

  const getIcon = (): ReactElement => {
    if (hasCircledIcon) {
      return (
        <Icon
          className={styles.circledIcon}
          icon={icon}
          shape={IconShape.CIRCLE}
          type={isHovered ? IconType.GHOST : IconType.FLAT}
          flatIconType={isHovered ? undefined : {color: FlatIconColor.PRIMARY, type: FlatIconColorType.DARK}}
          size={IconSize.SIZE_ICON_16}
        />
      );
    }

    return <Icon icon={icon} size={IconSize.SIZE_ICON_20} />;
  };

  const getItemContent = (): ReactElement => {
    return (
      <>
        {getIcon()}
        <Text val={label} size={TextSize.MD} bold />
      </>
    );
  };

  if (href !== undefined) {
    return (
      <a className={styles.navItem} href={href} onClick={handleClick} {...hoverHandlers}>
        {getItemContent()}
      </a>
    );
  }

  return (
    <ClickableDiv className={styles.navItem} onClick={handleClick} {...hoverHandlers}>
      {getItemContent()}
    </ClickableDiv>
  );
}
